import React, { useState, useEffect } from "react";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import {
  faHeart as faHeartSolid,
  faShoppingCart,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PublicProductList } from "interfaces/IProduct";
import { useAuthStore } from "../../../../zustand/loginStore";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useBreakpoints } from "hooks/useBreakPoints";
import { useNavigate } from "react-router-dom";
import { createAuthenticatedAxiosInstance, toggleFavorite } from "api/auth";
import { useUserStore } from "../../../../zustand/userStore";
import { IconButton } from "@mui/material";
import Discount1 from "../../../../assets/Discount1.svg";
import Discount2 from "../../../../assets/Discount2.svg";

interface ProductCardProps {
  product: PublicProductList;
  className?: string;
  handleProductClick: (product: PublicProductList) => void;
  /**
   * call back function for parent component to show a toast
   */
  handleToast: (message: string, show: boolean, success: boolean) => void;
}

const RelatedProductCard: React.FC<ProductCardProps> = ({
  product,
  className,
  handleProductClick,
  handleToast,
}) => {
  const { favoriteProducts, setFavoriteProducts } = useUserStore();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const productImages = product.attributes[0]?.images || [];
  const [imageOpacity, setImageOpacity] = useState(1);
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const { isMobile } = useBreakpoints();
  const [tooltipOpacity, setTooltipOpacity] = useState(1);
  const navigate = useNavigate();
  const [heartDisabled, setHeartDisabled] = useState<boolean>(false);
  const hasDiscount = !!product.discount_type;
  const isPercentOff = product.discount_type.includes("%");
  const [isFavorite, setIsFavorite] = useState(false);

  const isVisiblePosition =
    className?.includes("position-1") ||
    className?.includes("position-2") ||
    className?.includes("position-3") ||
    (isMobile && className?.includes("position-4"));

  /**
   * Handles the heart icon click event to display and auto-hide the tooltip
   *
   * The function:
   * 1. Sets the tooltip visibility state to true, making it appear immediately
   * 2. Creates a timer that will execute after 2000 milliseconds (2 seconds)
   * 3. When the timer completes, sets the tooltip visibility to false, hiding it
   *
   * This creates a smooth user experience where:
   * - User clicks heart icon
   * - Login message appears instantly
   * - Message automatically disappears after 2 seconds
   */
  const handleHeartClick = () => {
    setShowTooltip(true);
    setTooltipOpacity(1);

    setTimeout(() => {
      setTooltipOpacity(0);
      setTimeout(() => {
        setShowTooltip(false);
      }, 1000);
    }, 1500);
  };

  /**
   * Handles the click event on the favorite heart icon
   * Only triggers if user is authenticated (has token)
   * Toggles the favorite state for the product
   */
  const handleFavoriteClick = async (
    e: React.MouseEvent,
    productId: number
  ) => {
    setHeartDisabled(true);
    e.stopPropagation();
    if (token) {
      const { data, success } = await toggleFavorite(axiosInstance, productId);
      if (!success) {
        handleToast(
          `El producto no pudo ser ${
            isFavorite ? "removido de" : "agregado a"
          } a la lista de favoritos, por favor intente nuevamente.`,
          true,
          false
        );
        setHeartDisabled(false);
        return;
      }
      data && setFavoriteProducts(data);
      handleToast(
        `El producto fue ${
          isFavorite ? "removido de" : "agregado a"
        } la lista de favoritos.`,
        true,
        true
      );
      data && setFavoriteProducts(data);
      setHeartDisabled(false);
    }
  };

  /**
   * Tooltip component shown when non-authenticated users click the favorite icon
   * Displays a message encouraging user registration
   */
  const favoriteTooltip = (
    <Tooltip
      id="favorite-tooltip"
      className="custom-tooltip-client"
      style={{
        opacity: tooltipOpacity,
        lineHeight: "1.3",
        transition: "opacity 0.5s ease-in-out",
      }}
    >
      Inicia sesión o crea
      <br />
      una cuenta para
      <br />
      guardar tus artículos
      <br />
      favoritos!
    </Tooltip>
  );

  /**
   * Heart icon component that changes appearance based on favorite state
   * Shows solid heart when favorited, regular heart when not
   */
  const heartIcon = (product_id: number) => (
    <IconButton
      onClick={(e) => handleFavoriteClick(e, product_id)}
      disabled={heartDisabled}
      style={{ color: "#997404" }}
    >
      <FontAwesomeIcon
        icon={isFavorite ? faHeartSolid : faHeartRegular}
        className="icon"
        style={{ cursor: "pointer" }}
      />
    </IconButton>
  );

  /**
   * modular component to show discount quantities in case the product has discount
   */
  const discountInfo = (product: PublicProductList) => {
    return (
      <>
        <span className="price">
          ${product.attributes[0]?.sizes[0]?.new_price || 0}
        </span>
        {isPercentOff ? (
          <span className="discount-quantity">
            -{Math.trunc(product.discount)}
            {isPercentOff && "%"}
          </span>
        ) : (
          <span className="discount-quantity">- ${product.discount}</span>
        )}
      </>
    );
  };

  /**
   * Effect hook to handle image carousel functionality
   * Cycles through product images when component is hovered
   * Only activates if product has multiple images and is being hovered
   */
  useEffect(() => {
    if (!productImages || productImages.length <= 1 || !isHovered) return;

    const timer = setInterval(() => {
      setImageOpacity(0);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
        setImageOpacity(1);
      }, 500);
    }, 1500);

    return () => clearInterval(timer);
  }, [productImages, isHovered]);

  /**
   * effect for favorites to find them in the store
   */
  useEffect(() => {
    setIsFavorite(false);
    favoriteProducts.forEach((fav) => {
      if (product.id === fav.product_id) {
        setIsFavorite(true);
        return;
      }
    });
  }, [favoriteProducts]);
  return (
    <div
      className={`product-card ${className || ""}`}
      onClick={() => {
        if (isVisiblePosition) {
          window.scrollTo({ top: 0, behavior: "smooth" });
          navigate(`/product-details/${product.id}`);
        }
      }}
      onMouseEnter={() => isVisiblePosition && setIsHovered(true)}
      onMouseLeave={() => isVisiblePosition && setIsHovered(false)}
      style={{
        border:
          isVisiblePosition && isHovered
            ? "3px solid #997404"
            : "3px solid transparent",
        transition: "border-color 0.3s ease-in-out",
      }}
    >
      <>
        {hasDiscount && (
          <img
            src={isPercentOff ? Discount1 : Discount2}
            height={50}
            width={50}
            className="discount-tag"
          />
        )}
      </>
      <div className="image-container">
        <img
          src={productImages[currentImageIndex] || ""}
          alt={product.name}
          style={{ "--opacity": imageOpacity } as React.CSSProperties}
        />
      </div>
      <div className="product-name-container">
        <h3 style={{ fontSize: isMobile ? "15px" : "20px" }}>{product.name}</h3>
      </div>
      {!isMobile && (
        <>
          <div className="separator-line" />
          <div className="sizes-container">
            <span className="size-tag">Tallas</span>
            {product.attributes[0]?.sizes.map((size) => (
              <span key={size.size} className="size-tag">
                {size.size}
              </span>
            ))}
          </div>
          <div className="separator-line" />
        </>
      )}
      <div className="bottom-section">
        <>
          {hasDiscount ? (
            discountInfo(product)
          ) : (
            <span className="price">
              ${product.attributes[0]?.sizes[0]?.price || 0}
            </span>
          )}
        </>
        <div className="icons">
          {token ? (
            heartIcon(product.id)
          ) : (
            <OverlayTrigger
              show={showTooltip}
              trigger="click"
              placement="top"
              overlay={favoriteTooltip}
              rootClose
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleHeartClick();
                }}
                style={{
                  cursor: isVisiblePosition ? "pointer" : "default",
                  color: "#997404",
                }}
              >
                {heartIcon(product.id)}
              </IconButton>
            </OverlayTrigger>
          )}
          {!isMobile && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleHeartClick();
              }}
              style={{
                cursor: isVisiblePosition ? "pointer" : "default",
                color: "#997404",
              }}
            >
              <FontAwesomeIcon
                icon={faShoppingCart}
                className="icon"
                style={{
                  cursor: isVisiblePosition ? "pointer" : "default",
                  color: "#997404",
                  height: "24px",
                  width: "24px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  isVisiblePosition && handleProductClick(product);
                }}
              />
            </IconButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelatedProductCard;
